import { CalendarX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { isReadOnlyAuditor, useExpensesPermissions } from '../use-expenses-permissions';
import { monthLabel } from '../list/list-query';
import { MonthView } from './MonthView';
import { useMonth, useMonths } from './use-closings';

/** The picker's months: the list's, plus the one asked for by the address when the list lacks it. */
function monthOptions(items: readonly { month: string }[], chosen: string | undefined): string[] {
  const all = items.map((item) => item.month);
  if (chosen === undefined || all.includes(chosen)) return all;
  return [chosen, ...all];
}

function MonthPicker({
  options,
  value,
  onSelect,
}: {
  readonly options: readonly string[];
  readonly value: string | undefined;
  readonly onSelect: (month: string) => void;
}) {
  const { t, i18n } = useTranslation('closing');
  const id = useId();
  return (
    <div className="flex max-w-60 flex-col gap-1">
      <label htmlFor={id} className="text-sm font-semibold">
        {t('month')}
      </label>
      <Select value={value ?? ''} onValueChange={onSelect}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {monthLabel(option, i18n.resolvedLanguage)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** «Chiusure»: the monthly closing for Gestione and the Approvatore, read only for the Revisore. */
export function ClosingPage() {
  const { t } = useTranslation('closing');
  const titleId = useId();
  const permissions = useExpensesPermissions();
  const readOnly = isReadOnlyAuditor(permissions);
  const months = useMonths();
  const month = useMonth(months.month);

  const head = (
    <PageHead
      titleId={titleId}
      title={t('title')}
      summary={t('summary')}
      actions={
        readOnly ? (
          <span className="read-only-marker text-muted-foreground text-sm">{t('readOnly')}</span>
        ) : undefined
      }
    />
  );
  const failed = months.state.status === 'error' || month.state.status === 'error';
  if (failed) {
    return (
      <section aria-labelledby={titleId} className="flex flex-col gap-4">
        {head}
        <StateMessage
          role="alert"
          tone="error"
          icon={CalendarX}
          title={t('loadError')}
          action={
            <Button
              variant="outline"
              onClick={() => (months.state.status === 'error' ? months.reload() : month.reload())}
            >
              {t('retry')}
            </Button>
          }
        />
      </section>
    );
  }

  const items = months.state.status === 'ready' ? months.state.items : [];
  const options = monthOptions(items, months.month);

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      {head}
      {months.state.status === 'ready' && options.length === 0 ? (
        <StateMessage icon={CalendarX} title={t('noMonths')} />
      ) : (
        <MonthPicker options={options} value={months.month} onSelect={months.select} />
      )}
      {month.state.status === 'ready' ? (
        <MonthView
          key={month.state.month.month}
          month={month.state.month}
          readOnly={readOnly}
          adopt={month.adopt}
          reload={month.reload}
        />
      ) : null}
      {months.state.status === 'ready' &&
      month.state.status === 'loading' &&
      months.month !== undefined ? (
        <Skeleton role="status" aria-label={t('loading')} className="h-40 w-full" />
      ) : null}
      {months.state.status === 'loading' ? (
        <Skeleton role="status" aria-label={t('loading')} className="h-40 w-full" />
      ) : null}
    </section>
  );
}
